import { useEffect, useState } from 'react'
import { userApi, clientApi, ApiKeyInfo } from '../services/api'
import { Key, Copy, Trash2, Plus, Eye, EyeOff, CheckCircle, XCircle, RefreshCw, Zap } from 'lucide-react'
import toast from 'react-hot-toast'
// import { ApiTester } from '../components/ApiTester'
import { ConfirmDialog } from '../components/ConfirmDialog'

export function ApiKeysPage() {
  const [apiKeyInfo, setApiKeyInfo] = useState<ApiKeyInfo | null>(null)
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string>('')
  const [showNewKey, setShowNewKey] = useState(false)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newKeyTitle, setNewKeyTitle] = useState('')
  const [testingApiKey, setTestingApiKey] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} })

  useEffect(() => {
    loadApiKeyInfo()
  }, [])

  const loadApiKeyInfo = async () => {
    try {
      const info = await userApi.getApiKeyInfo()
      setApiKeyInfo(info)
    } catch (error) {
      console.error('Failed to load API key info:', error)
      toast.error('Failed to load API key information')
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateApiKey = async () => {
    setGenerating(true)
    try {
      const result = await userApi.generateApiKey(newKeyTitle || undefined)

      if (result.apiKey) {
        setNewlyCreatedKey(result.apiKey)
        setShowNewKey(true)
        setShowCreateForm(false)
        setNewKeyTitle('')
        toast.success('New API key generated! Make sure to copy it now.')
      } else {
        toast.error(result.message)
      }

      await loadApiKeyInfo()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to generate API key')
    } finally {
      setGenerating(false)
    }
  }

  const handleRevokeApiKey = async (apiKeyId: string, title: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke API Key',
      message: `Are you sure you want to revoke "${title}"? This will break any applications using it.`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        setRevokingId(apiKeyId)
        try {
          await userApi.revokeApiKey(apiKeyId)
          toast.success('API key revoked successfully')
          await loadApiKeyInfo()
        } catch (error: any) {
          toast.error(error.response?.data?.error || 'Failed to revoke API key')
        } finally {
          setRevokingId(null)
        }
      },
    })
  }

  const handleCopyApiKey = () => {
    if (newlyCreatedKey) {
      navigator.clipboard.writeText(newlyCreatedKey)
      toast.success('API key copied to clipboard!')
    }
  }

  const handleTestApiKey = async () => {
    if (!newlyCreatedKey) {
      toast.error('No API key to test. Generate one first.')
      return
    }

    setTestingApiKey(true)
    try {
      await clientApi.testApiKey(newlyCreatedKey)
      toast.success('✅ API key is working correctly!')
    } catch (error: any) {
      toast.error('❌ API key test failed: ' + (error.response?.data?.error || error.message))
    } finally {
      setTestingApiKey(false)
    }
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-12 w-12"></div>
      </div>
    )
  }

  const activeKeys = apiKeyInfo?.apiKeys?.filter(k => k.isActive) || []
  const inactiveKeys = apiKeyInfo?.apiKeys?.filter(k => !k.isActive) || []

  return (
    <div className="max-w-6xl mx-auto space-y-8 animate-fade-in-up">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant="danger"
        confirmText="Revoke Key"
      />
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-text mb-2">
          <span className="text-gradient">API Keys</span>
        </h1>
        <p className="text-text-muted">
          Generate and manage your API keys to access the Softitect API
        </p>
      </div>

      {/* Newly Created Key Banner */}
      {newlyCreatedKey && (
        <div className="card p-6 border border-success/30 bg-gradient-to-br from-success/5 to-accent-2/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-success" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-text">API Key Created Successfully</h3>
                <p className="text-xs text-text-muted">Save this key now - you won't see it again</p>
              </div>
            </div>
            <button
              onClick={() => { setNewlyCreatedKey(''); setShowNewKey(false) }}
              className="text-text-muted hover:text-text transition-colors p-1"
              aria-label="Dismiss"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  readOnly
                  value={showNewKey ? newlyCreatedKey : '••••••••••••••••••••••••••••••••••••••••'}
                  className="w-full px-4 py-3 bg-bg/50 border border-border rounded-lg font-mono text-sm text-text focus:outline-none pr-20"
                />
                <button
                  onClick={() => setShowNewKey(!showNewKey)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-text-muted hover:text-text transition-colors"
                  aria-label={showNewKey ? 'Hide API key' : 'Show API key'}
                >
                  {showNewKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleCopyApiKey}
                  className="px-4 py-3 bg-accent/10 hover:bg-accent/20 text-accent border border-accent/30 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium"
                  aria-label="Copy API key"
                >
                  <Copy className="w-4 h-4" />
                  <span className="hidden sm:inline">Copy</span>
                </button>
                <button
                  onClick={handleTestApiKey}
                  disabled={testingApiKey}
                  className="px-4 py-3 bg-success/10 hover:bg-success/20 text-success border border-success/30 rounded-lg transition-all duration-200 flex items-center gap-2 font-medium disabled:opacity-50"
                  aria-label="Test API key"
                >
                  {testingApiKey ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span className="hidden sm:inline">Testing...</span>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      <span className="hidden sm:inline">Test</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create New Key */}
      <div className="card p-6">
        {showCreateForm ? (
          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-text mb-1">Create New API Key</h3>
              <p className="text-sm text-text-muted">Give your API key a memorable name</p>
            </div>
            <div>
              <label htmlFor="key-title" className="block text-sm font-medium text-text mb-2">
                Key Name
              </label>
              <input
                id="key-title"
                type="text"
                value={newKeyTitle}
                onChange={(e) => setNewKeyTitle(e.target.value)}
                placeholder="e.g. Production Server, Development, My App"
                className="input-field"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={handleGenerateApiKey}
                disabled={generating}
                className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-accent/20"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Generate Key
                  </>
                )}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setNewKeyTitle('') }}
                className="px-5 py-2.5 bg-transparent hover:bg-panel border border-border text-text-muted hover:text-text rounded-lg font-medium transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-text mb-1">Your API Keys</h3>
              <p className="text-sm text-text-muted">
                {activeKeys.length} active key{activeKeys.length !== 1 ? 's' : ''}
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 shadow-lg shadow-accent/20"
            >
              <Plus className="w-4 h-4" />
              Create New Key
            </button>
          </div>
        )}
      </div>

      {/* API Keys List */}
      {apiKeyInfo?.apiKeys && apiKeyInfo.apiKeys.length > 0 ? (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" role="table">
              <thead>
                <tr className="border-b border-border bg-bg/50">
                  <th scope="col" className="text-left px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Name</th>
                  <th scope="col" className="text-left px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Created</th>
                  <th scope="col" className="text-left px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Status</th>
                  <th scope="col" className="text-right px-6 py-4 text-xs font-semibold text-text-muted uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeKeys.map((key) => (
                  <tr key={key.apiKeyId} className="border-b border-border hover:bg-bg/30 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Key className="w-4 h-4 text-accent" />
                        </div>
                        <div>
                          <div className="font-medium text-text">{key.title}</div>
                          <div className="text-xs text-text-muted font-mono">{key.apiKeyId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-muted">{formatDate(key.createDate)}</td>
                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-success/10 border border-success/20">
                        <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                        <span className="text-xs font-medium text-success">Active</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleRevokeApiKey(key.apiKeyId, key.title)}
                        disabled={revokingId === key.apiKeyId}
                        className="px-3 py-1.5 bg-destructive/10 hover:bg-destructive/20 text-destructive border border-destructive/30 rounded-lg transition-all duration-200 inline-flex items-center gap-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`Revoke ${key.title}`}
                      >
                        {revokingId === key.apiKeyId ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            Revoking...
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-3.5 h-3.5" />
                            Revoke
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                {inactiveKeys.map((key) => (
                  <tr key={key.apiKeyId} className="border-b border-border opacity-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-border flex items-center justify-center flex-shrink-0">
                          <Key className="w-4 h-4 text-text-muted" />
                        </div>
                        <div>
                          <div className="font-medium text-text-muted line-through">{key.title}</div>
                          <div className="text-xs text-text-muted font-mono">{key.apiKeyId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-text-muted">{formatDate(key.createDate)}</td>
                    <td className="px-6 py-4">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-border">
                        <XCircle className="w-3 h-3 text-text-muted" />
                        <span className="text-xs font-medium text-text-muted">Revoked</span>
                      </div>
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
            <Key className="w-8 h-8 text-accent" />
          </div>
          <h3 className="text-lg font-semibold text-text mb-2">No API Keys Yet</h3>
          <p className="text-text-muted mb-6">
            Create your first API key to start making requests to the Softitect API.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all duration-200 inline-flex items-center gap-2 shadow-lg shadow-accent/20"
          >
            <Plus className="w-4 h-4" />
            Create Your First Key
          </button>
        </div>
      )}

      {/* API Tester - Only show if user has an active API key */}
      {/*{activeKeys.length > 0 && (*/}
      {/*  <ApiTester apiKey={activeKeys[0].apiKeyId} />*/}
      {/*)}*/}
    </div>
  )
}
